import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ProjectDetail, ProjectSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { breadcrumbJsonLd, projectJsonLd } from '@/lib/structured-data'
import { getProject, getProjectSlugs, getRelatedProjects } from '@/lib/content/projects'
import MainContainer from '@/components/generals/main-container'
import EditorialGrid from '@/components/generals/editorial-grid'
import Breadcrumb from '@/components/elements/breadcrumb'
import SampleNotice from '@/components/elements/sample-notice'
import TextLink from '@/components/elements/text-link'
import DisplayHeading from '@/components/typography/display-heading'
import ProjectMetadata from '@/components/projects/project-metadata'
import ProjectFeature from '@/components/projects/project-feature'
import ProjectGallery from '@/components/projects/project-gallery'
import SchematicFigure from '@/components/content/schematic-figure'
import CoverPlate from '@/components/content/cover-plate'
import TableOfContents from '@/components/content/table-of-contents'
import Prose from '@/components/content/prose'

interface ProjectPageProps {
    project: ProjectDetail
    related: Array<ProjectSummary>
}

const ProjectPage: FC<ProjectPageProps> = ({ project, related }): ReactNode => {
    const path = `/projects/${project.slug}`

    return (
        <Layout>
            <Seo
                title={project.title}
                description={project.description}
                path={path}
                type='article'
                publishedTime={project.date}
                tags={project.technologies}
                jsonLd={[
                    projectJsonLd(project),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Work', path: '/projects' },
                        { name: project.title, path }
                    ])
                ]}
            />

            <article>
                <MainContainer as='header' className='pt-10 md:pt-14'>
                    <Breadcrumb
                        items={[
                            { label: 'Work', href: '/projects' },
                            { label: project.categoryLabel, href: `/projects?category=${project.category}` },
                            { label: project.title }
                        ]}
                    />

                    <EditorialGrid className='mt-10 gap-y-10 md:mt-14'>
                        <div className='col-span-4 flex flex-col gap-6 md:col-span-7'>
                            <p className='flex items-center gap-2.5 font-mono text-label uppercase text-brand'>
                                <span aria-hidden='true' className='size-1.5 bg-brand' />
                                Case study / {project.categoryLabel}
                            </p>

                            <DisplayHeading as='h1' size='headline' className='animate-in fade-in slide-in-from-bottom-2 duration-700 ease-out'>
                                {project.title}
                            </DisplayHeading>

                            <p className='max-w-[48ch] text-lede text-muted-foreground'>
                                {project.description}
                            </p>
                        </div>

                        <ProjectMetadata
                            project={project}
                            readingTime={project.readingTime}
                            className='col-span-4 md:col-span-5 md:self-end lg:col-span-4 lg:col-start-9'
                        />
                    </EditorialGrid>

                    {project.sample && (
                        <SampleNotice kind='case study' file={`content/projects/${project.slug}.md`} className='mt-10' />
                    )}
                </MainContainer>

                <MainContainer className='mt-10 md:mt-14'>
                    {project.cover ? (
                        <CoverPlate
                            id={`hero-${project.slug}`}
                            title={project.title}
                            schematic={null}
                            cover={project.cover}
                            coverAlt={project.coverAlt}
                            className='aspect-[16/9]'
                            sizes='100vw'
                            priority
                        />
                    ) : project.schematic ? (
                        <SchematicFigure schematic={project.schematic} id={`figure-${project.slug}`} figure='01' title='System overview' />
                    ) : null}
                </MainContainer>

                <MainContainer className='pt-14 pb-20 md:pt-20 md:pb-28'>
                    <EditorialGrid className='gap-y-10'>
                        <aside className='col-span-4 md:col-span-12 lg:col-span-3'>
                            <TableOfContents items={project.toc} className='lg:sticky lg:top-28' />
                        </aside>

                        <div className='col-span-4 md:col-span-10 lg:col-span-7 lg:col-start-5'>
                            <Prose html={project.html} className='max-w-reading' />

                            <p className='mt-12 flex items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                                End of case study
                                <span aria-hidden='true' className='size-2 bg-brand' />
                            </p>
                        </div>
                    </EditorialGrid>

                    <ProjectGallery images={project.gallery} className='mt-16' />
                </MainContainer>
            </article>

            {related.length > 0 && (
                <MainContainer as='section' className='pb-24 md:pb-32'>
                    <div className='flex items-center justify-between gap-4 border-t border-foreground pt-4'>
                        <h2 className='flex items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                            <span aria-hidden='true' className='size-1.5 bg-brand' />
                            Related projects
                        </h2>

                        <TextLink href='/projects' arrow mono>All projects</TextLink>
                    </div>

                    <div className='mt-10 grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-8'>
                        {related.map((item, index) => (
                            <ProjectFeature key={item.slug} project={item} index={index + 1} />
                        ))}
                    </div>
                </MainContainer>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: getProjectSlugs().map((slug) => ({ params: { slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<ProjectPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const project = await getProject(slug)

    if (!project) return { notFound: true }

    return {
        props: {
            project,
            related: getRelatedProjects(slug, 2)
        }
    }
}

export default ProjectPage
